import { useEffect, useState } from "react";
import { http } from "../lib/api";

const CACHE_KEY = "pr_iod_v1";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 min — server refreshes only when admin curates

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (Date.now() - (obj.t || 0) > CACHE_TTL_MS) return null;
    return obj.image || null;
  } catch {
    return null;
  }
}

function writeCache(image) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), image }));
  } catch {
    /* quota / private mode */
  }
}

/** Compact "Imagen del día" card — sidebar-sized (curated photo only). */
export default function ImageOfDay() {
  // Hydrate from sessionStorage immediately so the slot doesn't flicker when
  // the user navigates between pages or the API is slow.
  const [data, setData] = useState(() => readCache());
  const [loaded, setLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    http
      .get("/image-of-day", { timeout: 8000 })
      .then(({ data: payload }) => {
        if (cancelled) return;
        const next = payload?.image || null;
        // Only update if changed (so the cached image keeps showing while
        // the network is slow but eventually reconciles when it returns).
        setData((prev) => {
          if (next && (!prev || prev.article_id !== next.article_id)) {
            writeCache(next);
            // Reset image-load state when the article changes.
            setLoaded(false);
            setImgFailed(false);
            return next;
          }
          if (!next && prev) {
            // Server says cleared. Drop the cache too.
            sessionStorage.removeItem(CACHE_KEY);
            return null;
          }
          return prev;
        });
      })
      .catch(() => {
        // keep whatever we already have from cache; don't blank the slot
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;

  return (
    <section
      className="border-t-2 border-double border-ink pt-3 mt-6"
      data-testid="image-of-day"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-rojo mb-2">
        La imagen del día
      </p>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
        data-testid="image-of-day-link"
      >
        {!imgFailed && (
          <div className="relative bg-ink/5 overflow-hidden border border-ink/20 aspect-[4/3]">
            <img
              src={data.image_url}
              alt={data.caption || data.title}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setLoaded(true)}
              onError={() => setImgFailed(true)}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ${
                loaded ? "opacity-100" : "opacity-0"
              } group-hover:scale-[1.02]`}
              data-testid="image-of-day-img"
            />
            {!loaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-mono text-[9px] uppercase tracking-widest text-sub">cargando…</span>
              </div>
            )}
          </div>
        )}
        <p className="font-serif-display text-[15px] leading-snug mt-2 group-hover:text-rojo transition-colors line-clamp-3">
          {data.caption || data.title}
        </p>
      </a>
      <p className="font-mono text-[10px] uppercase tracking-widest text-sub mt-1.5">
        Foto: {data.source_name || "—"}
      </p>
      <a
        href={data.url}
        target="_blank"
        rel="noopener noreferrer"
        className="font-mono text-[10px] uppercase tracking-widest text-rojo hover:underline mt-1 inline-block"
      >
        Ver nota original ↗
      </a>
    </section>
  );
}
